import React, { useRef, useState, useEffect } from 'react';
import { Button, Card, Typography, Space, message } from 'antd';
import { useLocation } from 'react-router-dom';
import { CameraOutlined, CheckCircleOutlined, ReloadOutlined, RobotOutlined, SmileOutlined } from '@ant-design/icons';

const { Title, Paragraph } = Typography;

interface PhotoCaptureProps {
    isSubmitting: boolean;
    onCapturePhoto: (photoBlob: Blob) => void;
    onRetake: () => void;
    capturedPhoto: string | null;
    onSavePhoto?: (photoBlob: Blob) => void;
    testDetails?: any;
}

const PhotoCapture: React.FC<PhotoCaptureProps> = ({
    isSubmitting,
    onCapturePhoto,
    onRetake,
    capturedPhoto,
    onSavePhoto,
    testDetails
}) => {
    const location = useLocation();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const capturedBlobRef = useRef<Blob | null>(null);
    const [isCapturing, setIsCapturing] = useState(false);
    const [isVideoLoading, setIsVideoLoading] = useState(true);

    // Prevent recording during system check
    const isSystemCheck = location.pathname.includes('/system-check');

    // Initialize video stream when component mounts
    useEffect(() => {
        // Prevent recording during system check
        if (isSystemCheck) {
            console.log('System check in progress - video stream disabled');
            setIsVideoLoading(false);
            return;
        }

        const initializeVideoStream = async () => {
            try {
                setIsVideoLoading(true);
                // Request camera and microphone access
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                        facingMode: 'user'
                    },
                    audio: true
                });

                streamRef.current = stream;

                // Show video preview - ensure video element is ready
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;

                    // Wait for video metadata to load before playing
                    videoRef.current.onloadedmetadata = () => {
                        if (videoRef.current) {
                            videoRef.current.play()
                                .then(() => {
                                    setIsVideoLoading(false);
                                    console.log('Video stream started successfully');
                                })
                                .catch(err => {
                                    console.error('Error playing video:', err);
                                    setIsVideoLoading(false);
                                });
                        }
                    };
                } else {
                    // If videoRef is not ready, try again after a short delay
                    setTimeout(() => {
                        if (videoRef.current && streamRef.current) {
                            videoRef.current.srcObject = streamRef.current;
                            videoRef.current.onloadedmetadata = () => {
                                if (videoRef.current) {
                                    videoRef.current.play()
                                        .then(() => setIsVideoLoading(false))
                                        .catch(err => {
                                            console.error('Error playing video:', err);
                                            setIsVideoLoading(false);
                                        });
                                }
                            };
                        }
                    }, 100);
                }
            } catch (error: any) {
                console.error('Error initializing video stream:', error);
                setIsVideoLoading(false);
                message.error('Failed to access camera. Please allow camera permissions and refresh the page.');
            }
        };

        initializeVideoStream();

        // Cleanup: stop stream when component unmounts
        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
                streamRef.current = null;
            }
            if (videoRef.current) {
                videoRef.current.srcObject = null;
            }
            setIsVideoLoading(false);
        };
    }, [isSystemCheck]);

    // Ensure video stream is active when retaking (when capturedPhoto becomes null)
    useEffect(() => {
        // Prevent recording during system check
        if (isSystemCheck) {
            return;
        }

        if (!capturedPhoto && videoRef.current && streamRef.current) {
            // Reattach stream and play video when retaking
            if (videoRef.current.srcObject !== streamRef.current) {
                videoRef.current.srcObject = streamRef.current;
            }
            videoRef.current.play()
                .then(() => {
                    console.log('Video resumed after retake');
                })
                .catch(err => {
                    console.error('Error playing video after retake:', err);
                });
        }
    }, [capturedPhoto, isSystemCheck]);

    const capturePhoto = () => {
        // Prevent recording during system check
        if (isSystemCheck) {
            console.warn('Photo capture blocked: System check in progress');
            message.warning('Photo capture is disabled during system check');
            return;
        }

        if (!videoRef.current || !canvasRef.current) {
            message.error('Camera not ready. Please wait...');
            return;
        }

        setIsCapturing(true);
        try {
            const video = videoRef.current;
            const canvas = canvasRef.current;

            // Set canvas dimensions to match video
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            // Draw video frame to canvas
            const ctx = canvas.getContext('2d');
            if (ctx) {
                // Note: Video preview is mirrored with CSS (transform: scaleX(-1)) for user comfort,
                // but the actual video stream is not flipped, so we draw it normally
                // The captured photo will be in the correct orientation (not mirrored)
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                // Convert canvas to blob
                canvas.toBlob((blob) => {
                    if (blob) {
                        capturedBlobRef.current = blob; // Store blob for submission
                        onCapturePhoto(blob);
                        setIsCapturing(false);
                    } else {
                        message.error('Failed to capture photo');
                        setIsCapturing(false);
                    }
                }, 'image/jpeg', 0.95);
            }
        } catch (error) {
            console.error('Error capturing photo:', error);
            message.error('Failed to capture photo');
            setIsCapturing(false);
        }
    };

    return (
        <div className="bg-[#F3F7F7] flex h-screen flex-col overflow-hidden">
            {/* Header */}
            <div className="flex justify-between items-center border-b border-gray-200 p-3 bg-white flex-shrink-0">
                <img src={require('./../../../assets/logo/logo.png')} alt="logo" className="w-40" />
                <div className="w-20"></div>
            </div>

            <div className="flex-1 flex gap-4 p-4 overflow-hidden min-h-0">
                {/* First Column - AI Avatar */}
                <div className="w-1/3 flex flex-col gap-4 min-w-0 overflow-hidden">
                    {/* AI Avatar Area - Sufficient height */}
                    <div className="relative bg-black rounded-lg overflow-hidden flex items-center justify-center" style={{ minHeight: '350px', height: 'fit-content', padding: '40px' }}>
                        <div className="flex flex-col items-center justify-center">
                            <div className="w-32 h-32 rounded-full bg-gray-800 flex items-center justify-center mb-4">
                                <RobotOutlined style={{ fontSize: '64px', color: '#fff' }} />
                            </div>
                            <div className="text-white text-center">
                                <span className="text-lg font-semibold">AI Interviewer</span>
                            </div>
                        </div>
                    </div>

                    {/* User Video Preview for Photo Capture */}
                    {!capturedPhoto && (
                        <div className="relative bg-black rounded-lg overflow-hidden shadow-2xl border-4 border-gray-800" style={{ aspectRatio: '16/9' }}>
                            <video
                                ref={videoRef}
                                autoPlay
                                muted
                                playsInline
                                className="w-full h-full object-cover"
                                onLoadedMetadata={() => {
                                    if (videoRef.current && streamRef.current) {
                                        videoRef.current.play().catch(err => {
                                            console.error('Error playing video on metadata load:', err);
                                        });
                                    }
                                }}
                                onCanPlay={() => {
                                    setIsVideoLoading(false);
                                }}
                            />
                            {(isVideoLoading || !streamRef.current) && (
                                <div className="absolute inset-0 flex flex-col items-center justify-center text-white bg-gray-900">
                                    <span className="text-sm opacity-70">Camera Loading...</span>
                                </div>
                            )}
                            {/* Live indicator when camera is active */}
                            {streamRef.current && !isVideoLoading && (
                                <div className="absolute top-3 right-3 flex items-center gap-2 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
                                    <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                                    <span>LIVE</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Second Column - Photo Capture Content */}
                <div className="w-2/3 flex flex-col min-w-0 overflow-hidden">
                    <Card
                        className="flex-1 flex flex-col shadow-lg overflow-auto"
                        style={{ borderRadius: '12px' }}
                        bodyStyle={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '24px', overflow: 'auto' }}
                    >
                        <div className="flex-1 flex flex-col justify-center min-h-0">
                            {/* Header Section - Enhanced */}
                            <div className="mb-6 flex-shrink-0">
                                <div className="flex items-start gap-2 mb-2">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg flex-shrink-0">
                                        <CameraOutlined style={{ fontSize: '18px', color: '#fff' }} />
                                    </div>
                                    <div className="flex-1">
                                        <Title
                                            level={2}
                                            className="mb-0"
                                            style={{
                                                fontSize: '32px',
                                                fontWeight: 700,
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent',
                                                backgroundClip: 'text',
                                                letterSpacing: '-0.5px',
                                                lineHeight: '1'
                                            }}
                                        >
                                            Photo Capture
                                        </Title>
                                        <Paragraph
                                            className="text-sm text-gray-600 mb-0"
                                            style={{
                                                fontSize: '14px',
                                                lineHeight: '1.2',
                                                color: '#4b5563',
                                                marginTop: '0'
                                            }}
                                        >
                                            Please position yourself in front of the camera and smile for your profile photo.
                                        </Paragraph>
                                    </div>
                                </div>
                                <div className="w-20 h-0.5 bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 rounded-full"></div>
                            </div>

                            {/* Tips Box - Only show before capture */}
                            {!capturedPhoto && (
                                <div className="bg-gradient-to-r from-purple-50 via-pink-50 to-purple-50 border-2 border-purple-300 rounded-xl p-5 mb-6 shadow-md hover:shadow-lg transition-all duration-300 flex-shrink-0">
                                    <div className="flex items-start gap-4">
                                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center shadow-md">
                                            <SmileOutlined style={{ fontSize: '24px', color: '#fff' }} />
                                        </div>
                                        <div className="flex-1">
                                            <Paragraph className="text-base font-semibold text-gray-800 mb-3" style={{ fontSize: '16px' }}>
                                                Tips for a great photo:
                                            </Paragraph>
                                            <ul className="text-sm text-gray-700 space-y-2" style={{ listStyle: 'none', padding: 0 }}>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-purple-500 font-bold mt-1">•</span>
                                                    <span>Ensure good lighting</span>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-purple-500 font-bold mt-1">•</span>
                                                    <span>Look directly at the camera</span>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-purple-500 font-bold mt-1">•</span>
                                                    <span>Smile naturally</span>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="text-purple-500 font-bold mt-1">•</span>
                                                    <span>Keep your face centered in the frame</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Photo Preview and Controls - Consistent container */}
                            <div className="flex flex-col items-center gap-4 flex-1 justify-center min-h-0" style={{ minHeight: '300px' }}>
                                {capturedPhoto ? (
                                    <div className="w-full max-w-md">
                                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 mb-6 border-2 border-green-300 shadow-lg">
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                                                    <CheckCircleOutlined style={{ fontSize: '20px', color: '#fff' }} />
                                                </div>
                                                <span className="font-semibold text-gray-800" style={{ fontSize: '16px' }}>
                                                    Photo Captured Successfully
                                                </span>
                                            </div>
                                            <div className="rounded-lg overflow-hidden shadow-md border-2 border-green-200">
                                                <img
                                                    src={capturedPhoto}
                                                    alt="Captured"
                                                    className="w-full h-full object-cover"
                                                    style={{ maxHeight: '280px', display: 'block' }}
                                                />
                                            </div>
                                        </div>
                                        <Space size="large">
                                            <Button
                                                size="large"
                                                icon={<ReloadOutlined />}
                                                onClick={() => {
                                                    capturedBlobRef.current = null;
                                                    onRetake();
                                                }}
                                                style={{
                                                    height: '48px',
                                                    fontSize: '16px',
                                                    paddingLeft: '24px',
                                                    paddingRight: '24px',
                                                    borderRadius: '8px'
                                                }}
                                            >
                                                Retake Photo
                                            </Button>
                                            <Button
                                                type="primary"
                                                size="large"
                                                icon={<CheckCircleOutlined />}
                                                onClick={() => {
                                                    // Photo is already captured, submit it
                                                    if (capturedBlobRef.current && onSavePhoto) {
                                                        onSavePhoto(capturedBlobRef.current);
                                                    } else if (canvasRef.current && onSavePhoto) {
                                                        // Fallback: regenerate from canvas
                                                        canvasRef.current.toBlob((blob) => {
                                                            if (blob) {
                                                                onSavePhoto(blob);
                                                            }
                                                        }, 'image/jpeg', 0.95);
                                                    }
                                                }}
                                                loading={isSubmitting}
                                                style={{
                                                    height: '48px',
                                                    fontSize: '16px',
                                                    paddingLeft: '24px',
                                                    paddingRight: '24px',
                                                    borderRadius: '8px',
                                                    fontWeight: 500
                                                }}
                                            >
                                                Submit & Continue
                                            </Button>
                                        </Space>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center gap-4">
                                        <Button
                                            type="primary"
                                            size="large"
                                            icon={<CameraOutlined />}
                                            onClick={capturePhoto}
                                            loading={isCapturing}
                                            style={{
                                                height: '56px',
                                                fontSize: '18px',
                                                fontWeight: 600,
                                                paddingLeft: '40px',
                                                paddingRight: '40px',
                                                borderRadius: '12px',
                                                boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)',
                                                transition: 'all 0.3s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                e.currentTarget.style.boxShadow = '0 6px 16px rgba(24, 144, 255, 0.4)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(24, 144, 255, 0.3)';
                                            }}
                                        >
                                            {isCapturing ? 'Capturing...' : 'Capture Photo'}
                                        </Button>
                                        <Paragraph
                                            className="text-sm text-gray-500 text-center"
                                            style={{
                                                fontSize: '14px',
                                                marginTop: '0',
                                                fontStyle: 'italic'
                                            }}
                                        >
                                            Click the button above to capture your photo
                                        </Paragraph>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card>
                </div>
            </div>

            {/* Hidden canvas for photo capture */}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>
    );
};

export default PhotoCapture;
